import { useEffect, useMemo, useState, useRef } from 'react';

import { Chip, Tooltip } from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import TimerIcon from '@material-ui/icons/Timer';
import { useTimer } from 'react-timer-hook';

import { useAppDispatch } from 'store';
import {
  AtendimentoInterface,
  StatusSessaoEnum,
  updateListaDeIniciadosAlterarSessao,
} from 'store/reducers/atendimentos';

const useStyles = makeStyles(({ palette }) => ({
  chip: {
    color: palette.text.primary,
    backgroundColor: alpha(palette.text.primary, 0.1),
    paddingInline: 8,
    minWidth: 100,
  },
  icon: {
    color: palette.text.primary,
  },
  expiredChip: {
    color: palette.error.main,
    backgroundColor: alpha(palette.error.main, 0.1),
  },
  expiredIcon: {
    color: palette.error.main,
  },
}));

interface CronometroSessaoAtendimentoProps {
  atendimento: AtendimentoInterface | undefined;
}

export default function CronometroSessaoAtendimento({
  atendimento,
}: CronometroSessaoAtendimentoProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const isMountedRef = useRef(true);
  const dispatchSentRef = useRef(false);
  const lastRestartedDateRef = useRef<string | null>(null);

  const sessaoFechada = atendimento?.sessaoStatus === StatusSessaoEnum.CLOSED;
  const [expired, setExpired] = useState(sessaoFechada);

  const { minutes, hours, seconds, restart, pause } = useTimer({
    autoStart: false,
    expiryTimestamp: new Date(),
    onExpire: () => {
      if (!isMountedRef.current) return;
      if (dispatchSentRef.current) return;

      dispatchSentRef.current = true;
      setExpired(true);

      if (atendimento) {
        dispatch(
          updateListaDeIniciadosAlterarSessao({
            atendimentoId: atendimento.id,
            sessaoStatus: StatusSessaoEnum.CLOSED,
          }),
        );
      }
    },
  });

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pause();
    };
  }, [pause]);

  useEffect(() => {
    setExpired(sessaoFechada);
    dispatchSentRef.current = sessaoFechada;
  }, [sessaoFechada]);

  useEffect(() => {
    if (!atendimento?.sessaoExpiraEm) return;
    if (sessaoFechada) return;

    const expiryDate = new Date(atendimento.sessaoExpiraEm);
    const now = new Date();

    if (expiryDate <= now) {
      if (!dispatchSentRef.current && isMountedRef.current) {
        dispatchSentRef.current = true;
        setExpired(true);
        dispatch(
          updateListaDeIniciadosAlterarSessao({
            atendimentoId: atendimento.id,
            sessaoStatus: StatusSessaoEnum.CLOSED,
          }),
        );
      }
      return;
    }

    dispatchSentRef.current = false;
    setExpired(false);
    lastRestartedDateRef.current = atendimento.sessaoExpiraEm;
    restart(expiryDate);
  }, [
    atendimento?.sessaoExpiraEm,
    atendimento?.id,
    sessaoFechada,
    restart,
    dispatch,
  ]);

  const isUpdating = useMemo(() => {
    if (!atendimento || sessaoFechada || !atendimento.sessaoExpiraEm) {
      return !sessaoFechada;
    }

    if (atendimento.sessaoExpiraEm !== lastRestartedDateRef.current) {
      return true;
    }

    const expiryDate = new Date(atendimento.sessaoExpiraEm);
    const now = new Date();
    const isTimerZero = hours === 0 && minutes === 0 && seconds === 0;

    return expiryDate > now && isTimerZero;
  }, [atendimento, sessaoFechada, hours, minutes, seconds]);

  const renderLabel = useMemo(() => {
    if (expired) return 'Sessão fechada';

    if (isUpdating) return 'Atualizando...';

    if (hours === 0 && minutes === 0) return `${seconds}s`;

    const parts = [];
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    return parts.join(' e ');
  }, [hours, minutes, seconds, expired, isUpdating]);

  return (
    <Tooltip arrow title="Tempo restante da sessão">
      <Chip
        className={expired ? classes.expiredChip : classes.chip}
        label={renderLabel}
        icon={
          <TimerIcon className={expired ? classes.expiredIcon : classes.icon} />
        }
        size="small"
      />
    </Tooltip>
  );
}
